(() => {
  'use strict';

  const STORAGE_KEY = 'puresetup_first_order_popup';
  const SESSION_KEY = 'puresetup_first_order_popup_shown';
  const SELECTOR = '[data-ps-first-order-popup]';
  const PRIVACY_BANNER_SELECTORS = [
    '#shopify-pc__banner',
    '.shopify-pc__banner',
    '.shopify-pc__banner__dialog',
    '[data-shopify-privacy-banner]',
  ].join(',');
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const readState = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch (_error) {
      return {};
    }
  };

  const writeState = (state) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_error) {
      return;
    }
  };

  const sessionWasShown = () => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === 'true';
    } catch (_error) {
      return false;
    }
  };

  const markSessionShown = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, 'true');
    } catch (_error) {
      return;
    }
  };

  const initializePopup = (popup) => {
    if (popup.dataset.psFirstOrderInitialized === 'true') return;
    popup.dataset.psFirstOrderInitialized = 'true';

    const dialog = popup.querySelector('.ps-first-order__dialog');
    const emailInput = popup.querySelector('[data-ps-first-order-email]');
    const closeButtons = popup.querySelectorAll('[data-ps-first-order-close]');
    const copyButton = popup.querySelector('[data-ps-first-order-copy]');
    const code = popup.querySelector('[data-ps-first-order-code]');
    const designMode = popup.dataset.designMode === 'true';
    const success = Boolean(popup.querySelector('[data-ps-first-order-success]'));
    const hasFormError = Boolean(popup.querySelector('[data-ps-first-order-error]'));
    const delay = Math.max(0, Number(popup.dataset.delaySeconds) || 0) * 1000;
    const threshold = Math.min(100, Math.max(0, Number(popup.dataset.scrollThreshold) || 40));
    const dismissalDays = Math.max(1, Number(popup.dataset.dismissalDays) || 14);
    let lastFocusedElement = null;
    let timer = null;
    let privacyObserver = null;
    let privacyPoll = null;
    let pendingOpen = false;
    let open = false;

    if (!dialog) return;

    const getFocusableElements = () => Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));

    const isPrivacyBannerVisible = () =>
      Array.from(document.querySelectorAll(PRIVACY_BANNER_SELECTORS)).some((banner) => {
        const style = window.getComputedStyle(banner);
        return (
          !banner.hidden &&
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0' &&
          banner.getClientRects().length > 0
        );
      });

    const stopPrivacyWait = () => {
      privacyObserver?.disconnect();
      privacyObserver = null;
      if (privacyPoll) window.clearInterval(privacyPoll);
      privacyPoll = null;
    };

    const removeTriggers = () => {
      if (timer) window.clearTimeout(timer);
      timer = null;
      window.removeEventListener('scroll', handleScroll);
    };

    const revealPopup = () => {
      if (open) return;
      open = true;
      pendingOpen = false;
      stopPrivacyWait();
      removeTriggers();
      lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      popup.hidden = false;
      document.documentElement.classList.add('ps-first-order-lock');
      window.requestAnimationFrame(() => {
        popup.classList.add('is-open');
        const focusTarget = emailInput || getFocusableElements()[0] || dialog;
        focusTarget.focus({ preventScroll: true });
      });
      if (!designMode) markSessionShown();
    };

    const resumePendingOpen = () => {
      if (!pendingOpen || open || isPrivacyBannerVisible()) return;
      revealPopup();
    };

    const waitForPrivacyBanner = () => {
      if (privacyObserver || privacyPoll) return;
      privacyObserver = new MutationObserver(resumePendingOpen);
      privacyObserver.observe(document.documentElement, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'style', 'hidden'],
      });
      privacyPoll = window.setInterval(resumePendingOpen, 750);
    };

    const openPopup = () => {
      if (open || pendingOpen) return;
      if (!designMode && isPrivacyBannerVisible()) {
        pendingOpen = true;
        removeTriggers();
        waitForPrivacyBanner();
        return;
      }
      revealPopup();
    };

    const closePopup = (persistDismissal = true) => {
      if (!open) return;
      open = false;
      pendingOpen = false;
      stopPrivacyWait();
      popup.classList.remove('is-open');
      document.documentElement.classList.remove('ps-first-order-lock');
      if (persistDismissal && !designMode && !success) {
        const state = readState();
        state.dismissedUntil = Date.now() + dismissalDays * 24 * 60 * 60 * 1000;
        writeState(state);
      }
      popup.hidden = true;
      if (lastFocusedElement?.isConnected) lastFocusedElement.focus({ preventScroll: true });
    };

    function handleScroll() {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;
      const scrollProgress = (window.scrollY / scrollableHeight) * 100;
      if (scrollProgress >= threshold) openPopup();
    }

    const handleKeydown = (event) => {
      if (!open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closePopup();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    closeButtons.forEach((button) => button.addEventListener('click', () => closePopup()));
    document.addEventListener('keydown', handleKeydown);

    if (copyButton && code) {
      copyButton.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(code.textContent.trim());
          copyButton.textContent = 'COPIED';
        } catch (_error) {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(code);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      });
    }

    const state = readState();
    if (success) {
      state.subscribed = true;
      delete state.dismissedUntil;
      writeState(state);
      openPopup();
      return;
    }

    if (designMode) {
      openPopup();
      return;
    }

    if (hasFormError) {
      openPopup();
      return;
    }

    if (state.subscribed || Number(state.dismissedUntil) > Date.now() || sessionWasShown()) return;

    timer = window.setTimeout(openPopup, delay);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(initializePopup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const popup = event.target.querySelector(SELECTOR);
    if (popup) {
      popup.hidden = false;
      popup.classList.add('is-open');
      document.documentElement.classList.add('ps-first-order-lock');
      popup.querySelector('[data-ps-first-order-email], button, [tabindex]')?.focus({ preventScroll: true });
    }
  });
})();
